import type { OrganizationApiSecurityPolicy } from "@/stores/organization/organization-store";

export const SECURITY_POLICY_LIMITS = {
  sessionTimeout: { min: 300, max: 86400 },
  passwordMinLength: { min: 6, max: 128 },
  passwordExpirationDays: { min: 0, max: 365 },
  passwordPreventReuse: { min: 0, max: 24 },
};

const DEFAULT_SECURITY_POLICY: OrganizationApiSecurityPolicy = {
  passwordPolicy: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: false,
    expirationDays: 90,
    preventReuse: 5,
  },
  mfaRequired: false,
  mfaTypes: [],
  sessionTimeout: 1800,
  ipWhitelist: [],
  ipBlacklist: [],
  allowedDomains: [],
  ssoEnabled: false,
  dataRetentionDays: 365,
  encryptionRequired: true,
};

export const mergeSecurityPolicy = (
  securityPolicy: OrganizationApiSecurityPolicy | undefined,
): OrganizationApiSecurityPolicy => ({
  ...DEFAULT_SECURITY_POLICY,
  ...securityPolicy,
  passwordPolicy: {
    ...DEFAULT_SECURITY_POLICY.passwordPolicy,
    ...securityPolicy?.passwordPolicy,
  },
  mfaTypes: securityPolicy?.mfaTypes ?? [],
  ipWhitelist: securityPolicy?.ipWhitelist ?? [],
  ipBlacklist: securityPolicy?.ipBlacklist ?? [],
  allowedDomains: securityPolicy?.allowedDomains ?? [],
});

const isIntegerInRange = (value: number, min: number, max: number): boolean =>
  Number.isInteger(value) && value >= min && value <= max;

export const normalizeAllowedDomain = (value: string): string | null => {
  const domain = value.trim().toLowerCase();
  const domainPattern =
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

  if (
    !domain ||
    domain.includes("://") ||
    domain.includes("/") ||
    domain.includes(":") ||
    !domainPattern.test(domain)
  ) {
    return null;
  }

  return domain;
};

export const validateSecurityPolicy = (
  securityPolicy: OrganizationApiSecurityPolicy,
): string[] => {
  const errors: string[] = [];
  const { passwordPolicy } = securityPolicy;

  if (
    !isIntegerInRange(
      securityPolicy.sessionTimeout,
      SECURITY_POLICY_LIMITS.sessionTimeout.min,
      SECURITY_POLICY_LIMITS.sessionTimeout.max,
    )
  ) {
    errors.push("Session timeout must be between 300 and 86400 seconds.");
  }

  if (
    !isIntegerInRange(
      passwordPolicy.minLength,
      SECURITY_POLICY_LIMITS.passwordMinLength.min,
      SECURITY_POLICY_LIMITS.passwordMinLength.max,
    )
  ) {
    errors.push("Password minimum length must be between 6 and 128.");
  }

  if (
    !isIntegerInRange(
      passwordPolicy.expirationDays,
      SECURITY_POLICY_LIMITS.passwordExpirationDays.min,
      SECURITY_POLICY_LIMITS.passwordExpirationDays.max,
    )
  ) {
    errors.push("Password expiration must be between 0 and 365 days.");
  }

  if (
    !isIntegerInRange(
      passwordPolicy.preventReuse,
      SECURITY_POLICY_LIMITS.passwordPreventReuse.min,
      SECURITY_POLICY_LIMITS.passwordPreventReuse.max,
    )
  ) {
    errors.push("Password reuse prevention must be between 0 and 24.");
  }

  const invalidDomains = securityPolicy.allowedDomains.filter(
    (domain) => normalizeAllowedDomain(domain) !== domain,
  );

  if (invalidDomains.length > 0) {
    errors.push(
      "Allowed domains must be lowercase domains without protocols or paths.",
    );
  }

  return errors;
};
